import { useEffect, useRef, useState, type ReactNode } from 'react';
import type { DemoDescriptor, DemoType } from '../../shared/cardGuide';
import { COLORS } from '../../shared/constants';
import { bordersRegions, directionWedges, quadrantRegions } from '../../shared/cutGeometry';
import type { Boundary, LngLat } from '../../shared/protocol';

// ============================================================
// v7 — the animated card demo (full spec §5). A lightweight SVG/CSS
// stage: a stylized map (contour backdrop + zone + brand pin art) that
// loops a before → effect → after sequence for map cards, or a
// conceptual HUD beat for the rest. NO MapLibre, NO live game state —
// every coordinate here is a constant, so the demo tree renders with no
// SocketProvider (privacy is untouched; asserted by test). Honours
// prefers-reduced-motion (static "after" frame) and pauses off-screen.
// ============================================================

const SIG = '#e8451b';
const BONE = '#f5f1e6';
const SLATE = '#14171a';
const MOSS = '#5c7a6e';
const HAZARD = '#f5c842';
const PORTAL = COLORS.portal;

const W = 320;
const H = 200;

/** Reduced-motion / no-DOM ⇒ render the final ("after") frame statically. */
function prefersReduced(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return true;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

function fade(show: boolean, ms = 500): React.CSSProperties {
  return { opacity: show ? 1 : 0, transition: `opacity ${ms}ms ease` };
}

// ---- brand pin art (JSX twins of map/pins.ts, drawn centered) ----
function Seeker({ x, y, s = 1 }: { x: number; y: number; s?: number }) {
  return (
    <g transform={`translate(${x} ${y}) scale(${s})`}>
      <circle r="13" fill={BONE} fillOpacity="0.16" />
      <circle r="9" fill={BONE} stroke={SLATE} strokeWidth="2" />
      <circle r="3.4" fill={SLATE} />
    </g>
  );
}
function Hider({ x, y, style }: { x: number; y: number; style?: React.CSSProperties }) {
  return (
    <g transform={`translate(${x} ${y})`} style={style}>
      <circle r="15" fill={SIG} fillOpacity="0.16" />
      <circle r="9" fill={SIG} fillOpacity="0.3" />
      <path d="M2 -9 L-6 1 L-0.5 1.8 L-2.5 9 L6 -2 L0.5 -2.6 Z" fill={SIG} stroke={BONE} strokeWidth="0.7" strokeOpacity="0.5" strokeLinejoin="round" />
    </g>
  );
}
function ThermoStart({ x, y }: { x: number; y: number }) {
  return (
    <g transform={`translate(${x} ${y})`}>
      <circle r="8" fill="none" stroke={BONE} strokeWidth="2" strokeDasharray="3 2" opacity="0.85" />
      <circle r="2.4" fill={BONE} />
    </g>
  );
}

/** Faint topographic backdrop + the base "possible area" zone. */
function Field({ children }: { children?: ReactNode }) {
  return (
    <>
      <rect x="0" y="0" width={W} height={H} fill={SLATE} />
      <g fill="none" stroke={BONE} strokeWidth="0.7" opacity="0.12">
        {Array.from({ length: 6 }).map((_, i) => {
          const y = 18 + i * 34;
          return <path key={i} d={`M-20 ${y} Q 90 ${y - 16}, 180 ${y} T 360 ${y + 8}`} />;
        })}
      </g>
      {children}
    </>
  );
}
/** The full zone blob (before any clip). */
function Zone({ show = true }: { show?: boolean }) {
  return <rect x="34" y="26" width="252" height="148" rx="20" fill={SIG} fillOpacity="0.1" stroke={SIG} strokeOpacity="0.45" strokeWidth="1.5" style={fade(show)} />;
}

// ---- per-type renderers; `beat` is 0→1→2 (before→effect→after) ----
interface Ctx {
  beat: number;
  param?: number | string;
}

const SEEKER_C = { x: 160, y: 104 };

// ---- v11 §2K (BOLT-031): drift-proof demo geometry ----
// The filtering-card demo shapes are PROJECTED from the SAME shared cut
// builders the server resolves with (cutGeometry.ts). A hand-drawn demo can
// no longer lie about the cut: the registry test asserts these shapes ≡ the
// engine's regions on the same fixture input.
export const DEMO_S: LngLat = { lat: 33.1564, lng: -96.7267 };
const DEMO_M_PER_PX = 6; // stage scale: the 320 px stage spans ~1.9 km
const K_LAT = 111_320;
const K_LNG = 111_320 * Math.cos((DEMO_S.lat * Math.PI) / 180);

/** Geographic ring → SVG points around the stage seeker. */
export function projectRing(ring: number[][]): string {
  return ring
    .map(
      ([lng, lat]) =>
        `${(SEEKER_C.x + ((lng - DEMO_S.lng) * K_LNG) / DEMO_M_PER_PX).toFixed(1)},${(SEEKER_C.y - ((lat - DEMO_S.lat) * K_LAT) / DEMO_M_PER_PX).toFixed(1)}`,
    )
    .join(' ');
}
const unprojectPx = (x: number, y: number): [number, number] => [
  DEMO_S.lng + ((x - SEEKER_C.x) * DEMO_M_PER_PX) / K_LNG,
  DEMO_S.lat - ((y - SEEKER_C.y) * DEMO_M_PER_PX) / K_LAT,
];

/** The direction (X-wedges) / quadrants (N-S/E-W cross) demo regions. */
export function demoCutShapes(kind: 'direction' | 'quadrants'): { key: string; points: string }[] {
  const regions =
    kind === 'direction'
      ? directionWedges(DEMO_S).map((w) => ({ key: w.dir as string, ring: w.region.geometry.coordinates[0] }))
      : quadrantRegions(DEMO_S).map((q) => ({ key: q.corner as string, ring: q.region.geometry.coordinates[0] }));
  return regions.map((r) => ({ key: r.key, points: projectRing(r.ring as number[][]) }));
}

/** The borders demo: the stage zone rect unprojected to geography, run through
 *  the REAL 75/25 bisection, and projected back (§2L). */
export function demoBordersShapes(): { boundaryPoints: string; corePoints: string[] } {
  const corners: [number, number][] = [unprojectPx(34, 26), unprojectPx(286, 26), unprojectPx(286, 174), unprojectPx(34, 174)];
  const closed = [...corners, corners[0]];
  const boundary: Boundary = { type: 'Feature', properties: {}, geometry: { type: 'Polygon', coordinates: [closed] } };
  const r = bordersRegions(boundary);
  if (!r) return { boundaryPoints: projectRing(closed as unknown as number[][]), corePoints: [] };
  const rings = r.core.geometry.type === 'Polygon' ? [r.core.geometry.coordinates[0]] : r.core.geometry.coordinates.map((p) => p[0]);
  return { boundaryPoints: projectRing(closed as unknown as number[][]), corePoints: rings.map((ring) => projectRing(ring as number[][])) };
}

function radarDemo({ beat, param }: Ctx) {
  // param = radius ft → a display radius on the stage (bounded)
  const r = typeof param === 'number' ? Math.max(34, Math.min(96, param / 12)) : 70;
  return (
    <>
      <Field />
      <Zone show={beat < 2} />
      {/* effect: a dashed ring blooms from the seeker */}
      <circle
        cx={SEEKER_C.x}
        cy={SEEKER_C.y}
        r={r}
        fill="none"
        stroke={SIG}
        strokeWidth="2"
        strokeDasharray="5 4"
        style={{ opacity: beat >= 1 ? 0.95 : 0, transform: `scale(${beat >= 1 ? 1 : 0.2})`, transformOrigin: `${SEEKER_C.x}px ${SEEKER_C.y}px`, transition: 'opacity 400ms ease, transform 500ms ease' }}
      />
      {/* after: zone clipped to the disk (INSIDE) */}
      <circle cx={SEEKER_C.x} cy={SEEKER_C.y} r={r} fill={SIG} fillOpacity="0.16" stroke={SIG} strokeWidth="1.5" style={fade(beat >= 2)} />
      <Seeker x={SEEKER_C.x} y={SEEKER_C.y} />
    </>
  );
}

function directionDemo({ beat }: Ctx) {
  // v11 §2K field fix: the old animation drew axis-aligned quadrants while the
  // card cuts DIAGONAL-edged wedges (an X through the seeker). These shapes now
  // come from the shared cut geometry — the truth, by construction.
  const wedges = demoCutShapes('direction');
  const { x, y } = SEEKER_C;
  return (
    <>
      <Field />
      <Zone show={beat < 2} />
      {/* the X-cut: all four wedge outlines from the engine's own builders */}
      <g style={fade(beat >= 1)}>
        {wedges.map((w) => (
          <polygon key={w.key} points={w.points} fill="none" stroke={BONE} strokeOpacity="0.28" strokeWidth="1" />
        ))}
      </g>
      {/* after: the EAST wedge survives — diagonal edges, not axis lines */}
      <polygon points={wedges.find((w) => w.key === 'E')!.points} fill={SIG} fillOpacity="0.22" stroke={SIG} strokeWidth="1.4" style={fade(beat >= 2)} />
      <Seeker x={x} y={y} />
    </>
  );
}

function quadrantsDemo({ beat }: Ctx) {
  // v11 §2K: THIS card is the axis-aligned cross the field expected.
  const quads = demoCutShapes('quadrants');
  const { x, y } = SEEKER_C;
  return (
    <>
      <Field />
      <Zone show={beat < 2} />
      <g stroke={BONE} strokeOpacity="0.3" strokeWidth="1" style={fade(beat >= 1)}>
        <line x1={x} y1="8" x2={x} y2={H - 8} />
        <line x1="8" y1={y} x2={W - 8} y2={y} />
      </g>
      <polygon points={quads.find((q) => q.key === 'NE')!.points} fill={SIG} fillOpacity="0.22" stroke={SIG} strokeWidth="1.4" style={fade(beat >= 2)} />
      <Seeker x={x} y={y} />
    </>
  );
}

let bordersDemoCache: { boundaryPoints: string; corePoints: string[] } | null = null;
function bordersDemo({ beat }: Ctx) {
  // v11 §2L: the contour is the REAL bisection-solved 75% inset (cached — the
  // bisection runs ~48 negative buffers).
  bordersDemoCache ??= demoBordersShapes();
  const { boundaryPoints, corePoints } = bordersDemoCache;
  const toPath = (pts: string) => `M${pts.split(' ').join(' L')} Z`;
  return (
    <>
      <Field />
      <polygon points={boundaryPoints} fill={SIG} fillOpacity="0.08" stroke={SIG} strokeOpacity="0.45" strokeWidth="1.5" />
      {/* effect: the solved 25/75 inset contour */}
      <g style={fade(beat >= 1)}>
        {corePoints.map((pts, i) => (
          <polygon key={i} points={pts} fill="none" stroke={HAZARD} strokeWidth="1.6" strokeDasharray="4 3" />
        ))}
      </g>
      {/* after: the hider was in the BAND — the core is eliminated (jackpot) */}
      <path d={[toPath(boundaryPoints), ...corePoints.map(toPath)].join(' ')} fillRule="evenodd" fill={SIG} fillOpacity="0.22" style={fade(beat >= 2)} />
      <g style={fade(beat >= 2)}>
        {corePoints.map((pts, i) => (
          <polygon key={i} points={pts} fill={MOSS} fillOpacity="0.18" stroke={MOSS} strokeWidth="1" strokeDasharray="4 3" />
        ))}
      </g>
      <Seeker x={SEEKER_C.x} y={SEEKER_C.y} />
    </>
  );
}

/**
 * v24 BOLT-118 — the LANDMARK cards, one demo for all six.
 *
 * Three features, the map split by which one is nearest, then everything but
 * the winner's region eliminated. Deliberately schematic: the real partition is
 * a server-computed Voronoi, and the point of the demo is the IDEA (the map
 * divides by real things you can see), not the exact edges.
 */
function landmarkDemo({ beat }: Ctx) {
  const spots = [
    { x: 78, y: 62 },
    { x: 232, y: 74 },
    { x: 150, y: 156 },
  ];
  // Schematic cell borders: the three-way meeting point and its spokes.
  const hub = { x: 152, y: 96 };
  return (
    <>
      <Field />
      {/* setup: the real features on the map */}
      {spots.map((s, i) => (
        <g key={i} style={fade(true)}>
          <circle cx={s.x} cy={s.y} r="9" fill={MOSS} fillOpacity="0.35" stroke={MOSS} strokeWidth="1.4" />
        </g>
      ))}
      {/* effect: the map divides by which feature is closest */}
      <g style={fade(beat >= 1)} stroke={HAZARD} strokeWidth="1.6" strokeDasharray="4 3" fill="none">
        <line x1={hub.x} y1={hub.y} x2="150" y2="24" />
        <line x1={hub.x} y1={hub.y} x2="30" y2="150" />
        <line x1={hub.x} y1={hub.y} x2="286" y2="150" />
      </g>
      {/* after: only the winning feature's region survives */}
      <g style={fade(beat >= 2)}>
        <path d={`M${hub.x},${hub.y} L150,24 L30,24 L30,150 Z`} fill={SIG} fillOpacity="0.22" />
        <path d={`M${hub.x},${hub.y} L150,24 L286,24 L286,150 Z`} fill={SIG} fillOpacity="0.22" />
        <path d={`M${hub.x},${hub.y} L30,150 L286,150 Z`} fill={MOSS} fillOpacity="0.2" stroke={MOSS} strokeWidth="1.2" />
        <circle cx={spots[2].x} cy={spots[2].y} r="11" fill="none" stroke={BONE} strokeWidth="1.6" />
      </g>
      <Seeker x={SEEKER_C.x} y={SEEKER_C.y} />
    </>
  );
}

function thermometerDemo({ beat }: Ctx) {
  const startX = 96;
  const endX = 210;
  const y = 104; // the walk line — AND the slice line (the cut runs along your path)
  const sx = beat === 0 ? startX : endX;
  // v21 BOLT-101 — Slice: the cut is the LINE YOU WALK (start → end), extended
  // across the map, not its perpendicular bisector. The half without the hider is
  // removed. Here the walk is horizontal, so the map splits into an upper and a
  // lower half; the hider is to the south, so the LOWER half survives.
  const zx = 34;
  const zw = 252; // the Zone rect: x 34..286
  const zTop = 26;
  const zBot = 174;
  const zcx = zx + zw / 2;
  return (
    <>
      <Field />
      <Zone show={beat < 2} />
      {/* after: the map is sliced ALONG the walk; the hider's (lower) half survives */}
      <g style={fade(beat >= 2)}>
        {/* removed half — above the walk line, dimmed away */}
        <rect x={zx} y={zTop} width={zw} height={y - zTop} fill={BONE} fillOpacity="0.04" />
        {/* kept half — the hider's side, below the walk line */}
        <rect x={zx} y={y} width={zw} height={zBot - y} fill={MOSS} fillOpacity="0.22" />
        {/* the slice line — ALONG the walk, spanning the whole map */}
        <line x1={zx} y1={y} x2={zx + zw} y2={y} stroke={MOSS} strokeWidth="2" strokeDasharray="5 4" />
        <text x={zcx} y={y + 42} fill={MOSS} fontFamily="'JetBrains Mono', monospace" fontSize="11" fontWeight="700" textAnchor="middle">
          HIDER&#39;S SIDE
        </text>
        <text x={zcx} y={y - 28} fill={BONE} fontFamily="'JetBrains Mono', monospace" fontSize="10" fontWeight="700" textAnchor="middle" opacity="0.4">
          CUT AWAY
        </text>
      </g>
      <ThermoStart x={startX} y={y} />
      {/* trail from start to seeker — the path that becomes the cut */}
      <line x1={startX} y1={y} x2={sx} y2={y} stroke={BONE} strokeWidth="2" strokeDasharray="4 3" style={{ opacity: beat >= 1 ? 0.9 : 0, transition: 'opacity 300ms ease' }} />
      <g style={{ transition: 'transform 700ms ease', transform: `translateX(${sx - startX}px)` }}>
        <Seeker x={startX} y={y} />
      </g>
    </>
  );
}

// v15 BOLT-056 — the photo demo carries the card's IDENTITY, two beats:
// a camera, then a picture frame holding an abstract per-card subject
// (Jake: "simple / abstract and on brand… just representative"). The penalty
// mechanic lives in the codex copy, not the animation.
export const PHOTO_SUBJECTS = ['cloud', 'mailbox', 'house', 'feet', 'building', 'person'] as const;
export type PhotoSubject = (typeof PHOTO_SUBJECTS)[number];

function SubjectMark({ subject }: { subject: string }) {
  switch (subject) {
    case 'cloud':
      return (
        <g stroke={BONE} strokeWidth="2.4" fill="none" strokeLinecap="round">
          <path d="M128 106 a14 14 0 0 1 8 -26 a18 18 0 0 1 35 -6 a13 13 0 0 1 12 22 z" transform="translate(15 8)" />
        </g>
      );
    case 'mailbox':
      return (
        <g strokeLinecap="round">
          <rect x="134" y="78" width="42" height="26" rx="12" stroke={BONE} strokeWidth="2.4" fill="none" />
          <line x1="155" y1="104" x2="155" y2="124" stroke={BONE} strokeWidth="2.4" />
          <line x1="176" y1="84" x2="184" y2="76" stroke={SIG} strokeWidth="2.6" />
        </g>
      );
    case 'house':
      return (
        <g stroke={BONE} strokeWidth="2.4" fill="none" strokeLinecap="round" strokeLinejoin="round">
          <path d="M134 100 l26 -20 l26 20 v22 h-52 z" />
          <rect x="153" y="108" width="14" height="14" stroke={SIG} strokeWidth="2.2" />
        </g>
      );
    case 'feet':
      return (
        <g stroke={BONE} strokeWidth="2.4" fill="none">
          <ellipse cx="146" cy="100" rx="9" ry="17" transform="rotate(-8 146 100)" />
          <ellipse cx="174" cy="100" rx="9" ry="17" transform="rotate(8 174 100)" />
        </g>
      );
    case 'building':
      return (
        <g>
          <rect x="146" y="66" width="28" height="58" rx="2" stroke={BONE} strokeWidth="2.4" fill="none" />
          {[76, 90, 104].map((y) => (
            <g key={y}>
              <circle cx="154" cy={y} r="1.8" fill={SIG} />
              <circle cx="166" cy={y} r="1.8" fill={SIG} />
            </g>
          ))}
        </g>
      );
    default: // person (the selfie)
      return (
        <g stroke={BONE} strokeWidth="2.4" fill="none" strokeLinecap="round">
          <circle cx="160" cy="86" r="11" />
          <path d="M140 122 a20 20 0 0 1 40 0" />
        </g>
      );
  }
}

function photoDemo({ beat, param }: Ctx) {
  const subject = typeof param === 'string' ? param : 'cloud';
  return (
    <>
      <rect x="0" y="0" width={W} height={H} fill={SLATE} />
      <g fill="none" stroke={BONE} strokeWidth="0.7" opacity="0.1">
        {Array.from({ length: 5 }).map((_, i) => (
          <path key={i} d={`M-20 ${28 + i * 38} Q 120 ${12 + i * 38}, 360 ${28 + i * 38}`} />
        ))}
      </g>
      {/* beat 0: the camera — a photo is being demanded */}
      <g style={{ opacity: beat === 0 ? 1 : 0, transition: 'opacity 400ms ease' }}>
        <rect x="126" y="76" width="68" height="46" rx="8" stroke={BONE} strokeWidth="2.4" fill="none" />
        <path d="M148 76 l5 -8 h14 l5 8" stroke={BONE} strokeWidth="2.4" fill="none" />
        <circle cx="160" cy="99" r="13" stroke={SIG} strokeWidth="2.6" fill="none" />
        <circle cx="160" cy="99" r="3" fill={SIG} />
      </g>
      {/* beats 1+: the framed shot — the card's subject, abstract on-brand */}
      <g style={{ opacity: beat >= 1 ? 1 : 0, transition: 'opacity 400ms ease' }} data-testid={`photo-subject-${subject}`}>
        <rect x="112" y="52" width="96" height="84" rx="6" stroke={BONE} strokeWidth="2" fill={MOSS} fillOpacity="0.14" />
        <rect x="120" y="60" width="80" height="68" rx="3" stroke={BONE} strokeWidth="1" opacity="0.35" fill="none" />
        <SubjectMark subject={subject} />
      </g>
    </>
  );
}

function MockRail({ locked, label }: { locked: boolean; label: string }) {
  return (
    <>
      <rect x="0" y="0" width={W} height={H} fill={SLATE} />
      <g fill="none" stroke={BONE} strokeWidth="0.7" opacity="0.1">
        {Array.from({ length: 5 }).map((_, i) => (
          <path key={i} d={`M-20 ${28 + i * 38} Q 120 ${12 + i * 38}, 360 ${28 + i * 38}`} />
        ))}
      </g>
      {[0, 1, 2].map((i) => (
        <g key={i} style={{ transition: 'opacity 400ms ease', opacity: locked ? 0.32 : 1 }}>
          <rect x={36 + i * 86} y="118" width="68" height="56" rx="8" fill={locked ? '#22272c' : SIG} fillOpacity={locked ? 1 : 0.2} stroke={locked ? BONE : SIG} strokeOpacity={locked ? 0.18 : 0.6} strokeWidth="1.5" />
        </g>
      ))}
      {/* lock glyph + countdown */}
      <g style={fade(locked, 400)} transform={`translate(${W / 2} 64)`}>
        <rect x="-13" y="-6" width="26" height="20" rx="3" fill={SIG} />
        <path d="M-7 -6 v-5 a7 7 0 0 1 14 0 v5" fill="none" stroke={SIG} strokeWidth="3" />
        <circle cx="0" cy="3" r="2.6" fill={SLATE} />
      </g>
      <text x={W / 2} y="100" fill={BONE} fontFamily="'Archivo', sans-serif" fontSize="13" fontWeight="800" fontStyle="italic" textAnchor="middle" style={fade(locked, 400)}>
        {label} · LOCKED
      </text>
    </>
  );
}
// v19 — each timed curse gets a PURPOSE-BUILT demo (the old generic 'curse-time'
// showed a locked card rail for all four, which is only true of Silence).

/** Silence — the seekers cannot play ANY card (they can still move). The locked
 *  card rail IS the honest picture here, so it keeps the rail treatment. */
function silenceDemo({ beat }: Ctx) {
  return <MockRail locked={beat >= 1} label="SILENCE" />;
}

/** Freeze — the seekers STOP where they stand (movement blocked in every
 *  direction), but their cards stay live. A STOP sign is what they see. */
function freezeDemo({ beat }: Ctx) {
  const frozen = beat >= 1;
  const c = SEEKER_C;
  return (
    <>
      <Field />
      {/* movement blocked in every direction — short arrows, each slashed out */}
      <g stroke={SIG} strokeWidth="2.4" strokeLinecap="round" style={fade(frozen, 400)}>
        {[[1, 0], [-1, 0], [0, 1], [0, -1]].map(([dx, dy], i) => {
          const tipX = c.x + dx * 34;
          const tipY = c.y + dy * 34;
          return (
            <g key={i}>
              <line x1={c.x + dx * 18} y1={c.y + dy * 18} x2={tipX} y2={tipY} />
              <line x1={tipX - 5} y1={tipY - 5} x2={tipX + 5} y2={tipY + 5} />
              <line x1={tipX + 5} y1={tipY - 5} x2={tipX - 5} y2={tipY + 5} />
            </g>
          );
        })}
      </g>
      <Seeker x={c.x} y={c.y} />
      {/* the STOP sign the frozen seeker sees */}
      <g style={fade(frozen, 400)} transform={`translate(${c.x} 46)`}>
        <polygon points="-15,-6 -6,-15 6,-15 15,-6 15,6 6,15 -6,15 -15,6" fill={SIG} stroke={BONE} strokeWidth="1" />
        <text x="0" y="4" fill={BONE} fontFamily="'Archivo', sans-serif" fontSize="9" fontWeight="800" textAnchor="middle">STOP</text>
      </g>
      <text x={W / 2} y="180" fill={BONE} fontFamily="'JetBrains Mono', monospace" fontSize="9" fontWeight="700" textAnchor="middle" style={fade(frozen, 400)}>
        FROZEN IN PLACE · CARDS STILL ALLOWED
      </text>
    </>
  );
}

/** Babel — the seeker's card labels render in another language (real ja strings
 *  from src/shared/babel/strings.ts). Nothing is blocked; the WORDS change. */
function babelDemo({ beat }: Ctx) {
  const scrambled = beat >= 1;
  const cards = scrambled
    ? [{ big: 'レーダー', sub: '出す' }, { big: '温度計', sub: '出す' }, { big: '写真', sub: '要求' }] // the curated ja Babel words (BABEL_STRINGS.ja)
    : [{ big: 'RADIUS', sub: 'PLAY' }, { big: 'SLICE', sub: 'PLAY' }, { big: 'PHOTO', sub: 'DEMAND' }];
  return (
    <>
      <rect x="0" y="0" width={W} height={H} fill={SLATE} />
      <g fill="none" stroke={BONE} strokeWidth="0.7" opacity="0.1">
        {Array.from({ length: 5 }).map((_, i) => (
          <path key={i} d={`M-20 ${28 + i * 38} Q 120 ${12 + i * 38}, 360 ${28 + i * 38}`} />
        ))}
      </g>
      <text x={W / 2} y="42" fill={BONE} fontFamily="'JetBrains Mono', monospace" fontSize="9" fontWeight="700" textAnchor="middle" opacity="0.6">
        THE SEEKER&apos;S CARDS
      </text>
      {cards.map((card, i) => (
        <g key={i} transform={`translate(${46 + i * 78} 66)`}>
          <rect x="0" y="0" width="62" height="70" rx="8" fill={SIG} fillOpacity="0.14" stroke={SIG} strokeOpacity="0.5" strokeWidth="1.4" />
          <text x="31" y="34" fill={BONE} fontFamily="'Archivo', sans-serif" fontSize="12" fontWeight="800" fontStyle="italic" textAnchor="middle">
            {card.big}
          </text>
          <text x="31" y="55" fill={BONE} fontFamily="'JetBrains Mono', monospace" fontSize="9" fontWeight="700" textAnchor="middle" opacity="0.75">
            {card.sub}
          </text>
        </g>
      ))}
      <text x={W / 2} y="176" fill={SIG} fontFamily="'Archivo', sans-serif" fontSize="12" fontWeight="800" fontStyle="italic" textAnchor="middle" style={fade(scrambled, 400)}>
        BABEL · ANOTHER LANGUAGE
      </text>
    </>
  );
}

/** Flying Blind — the seeker's basemap tiles (contours, roads) go dark; only the
 *  play-area outline, the zone, and their own pin remain. */
function flyingBlindDemo({ beat }: Ctx) {
  const dark = beat >= 1;
  return (
    <>
      <rect x="0" y="0" width={W} height={H} fill={SLATE} />
      {/* the cartography that GOES DARK — contours + roads */}
      <g style={{ transition: 'opacity 500ms ease', opacity: dark ? 0.05 : 1 }}>
        <g fill="none" stroke={BONE} strokeWidth="0.9" opacity="0.16">
          {Array.from({ length: 6 }).map((_, i) => {
            const y = 18 + i * 34;
            return <path key={i} d={`M-20 ${y} Q 90 ${y - 16}, 180 ${y} T 360 ${y + 8}`} />;
          })}
        </g>
        <g stroke={BONE} strokeOpacity="0.3" strokeWidth="5" strokeLinecap="round">
          <line x1="20" y1="150" x2="300" y2="92" />
          <line x1="150" y1="188" x2="205" y2="24" />
          <line x1="40" y1="58" x2="286" y2="128" />
        </g>
      </g>
      {/* what STAYS: the play-area outline, the zone, the seeker's pin */}
      <rect x="34" y="26" width="252" height="148" rx="20" fill="none" stroke={SIG} strokeOpacity="0.55" strokeWidth="1.8" />
      <Zone />
      <Seeker x={SEEKER_C.x} y={SEEKER_C.y} />
      <text x={W / 2} y="18" fill={BONE} fontFamily="'JetBrains Mono', monospace" fontSize="9" fontWeight="700" textAnchor="middle" style={fade(dark, 400)}>
        MAP DARK · AREA + ZONE REMAIN
      </text>
    </>
  );
}

function portalDemo({ beat }: Ctx) {
  const entry = { x: 92, y: 132 };
  const exit = { x: 236, y: 70 };
  const seeker = beat === 0 ? { x: 60, y: 150 } : beat === 1 ? exit : { x: 270, y: 48 };
  return (
    <>
      <Field />
      {/* roads hint */}
      <g stroke={BONE} strokeOpacity="0.18" strokeWidth="6" strokeLinecap="round">
        <line x1="30" y1="150" x2="290" y2="92" />
        <line x1="150" y1="180" x2="200" y2="30" />
      </g>
      {/* entry (red) box */}
      <rect x={entry.x - 16} y={entry.y - 16} width="32" height="32" rx="4" fill={SIG} fillOpacity="0.18" stroke={SIG} strokeWidth="1.6" strokeDasharray="4 3" />
      {/* exit (green) beacon */}
      <g transform={`translate(${exit.x} ${exit.y})`}>
        <circle r="18" fill={PORTAL} fillOpacity="0.16" />
        <circle r="10" fill={PORTAL} fillOpacity="0.34" />
        <circle r="6" fill={PORTAL} stroke={SLATE} strokeWidth="2" />
      </g>
      {/* teleport flash on arrival */}
      <circle cx={exit.x} cy={exit.y} r="22" fill="none" stroke={PORTAL} strokeWidth="2" style={{ opacity: beat === 1 ? 0.9 : 0, transition: 'opacity 300ms ease' }} />
      <g style={{ transition: 'transform 600ms ease', transform: `translate(${seeker.x - 60}px, ${seeker.y - 150}px)` }}>
        <Seeker x={60} y={150} />
      </g>
      {/* mini lock glyph while transported */}
      <g style={fade(beat === 1, 300)} transform={`translate(${exit.x + 22} ${exit.y - 18})`}>
        <rect x="-7" y="-3" width="14" height="11" rx="2" fill={SIG} />
        <path d="M-4 -3 v-3 a4 4 0 0 1 8 0 v3" fill="none" stroke={SIG} strokeWidth="2" />
      </g>
    </>
  );
}

/** v19 BOLT-085 — the demo shows the hider's BANKED time (their score), which
 *  DERIVES from the card's param: it holds at the 12:00 base until the +N lands,
 *  then jumps to 12 + N minutes (+2 ⇒ 14:00 · +5 ⇒ 17:00 · +10 ⇒ 22:00).
 *  Exported pure for the unit pin — the old hard-coded 14:00 showed +2 on every
 *  bonus card, and the earlier 12:30 base implied a round-clock extension. */
export function bonusDemoClock(beat: number, amount: number): string {
  // v19 — the hider's BANKED time (their score), which the bonus adds to. NOT
  // the round clock: a bonus does not extend the round, it banks minutes toward
  // the final score. So it holds at the base until the +N lands, then jumps by N.
  const base = 12;
  return beat >= 2 ? `${base + amount}:00` : `${base}:00`;
}

function bonusDemo({ beat, param }: Ctx) {
  const amount = typeof param === 'number' ? param : 2;
  const label = `+${amount} MIN`;
  return (
    <>
      <rect x="0" y="0" width={W} height={H} fill={SLATE} />
      <g fill="none" stroke={BONE} strokeWidth="0.7" opacity="0.1">
        {Array.from({ length: 5 }).map((_, i) => (
          <path key={i} d={`M-20 ${28 + i * 38} Q 120 ${12 + i * 38}, 360 ${28 + i * 38}`} />
        ))}
      </g>
      {/* the hider's BANKED time (their score) — the bonus adds to THIS, not the
          live round clock (a bonus does not extend the round). */}
      <text x={W / 2} y="66" fill={MOSS} fontFamily="'JetBrains Mono', monospace" fontSize="9" fontWeight="700" textAnchor="middle" opacity="0.75">
        TIME BANKED
      </text>
      <text x={W / 2} y="102" fill={BONE} fontFamily="'JetBrains Mono', monospace" fontSize="30" fontWeight="700" textAnchor="middle">
        {bonusDemoClock(beat, amount)}
      </text>
      {/* the +N chip appears, then banks into the total */}
      <g style={{ transition: 'transform 650ms ease, opacity 400ms ease', transform: beat >= 2 ? 'translate(0, -46px) scale(0.7)' : 'translate(0,0)', opacity: beat === 1 ? 1 : 0 }}>
        <rect x="120" y="128" width="80" height="34" rx="8" fill={SIG} />
        <text x="160" y="150" fill={BONE} fontFamily="'Archivo', sans-serif" fontSize="14" fontWeight="800" fontStyle="italic" textAnchor="middle">
          {label}
        </text>
      </g>
    </>
  );
}

function vetoDemo({ beat }: Ctx) {
  const gone = beat >= 2;
  return (
    <>
      <rect x="0" y="0" width={W} height={H} fill={SLATE} />
      <g fill="none" stroke={BONE} strokeWidth="0.7" opacity="0.1">
        {Array.from({ length: 5 }).map((_, i) => (
          <path key={i} d={`M-20 ${28 + i * 38} Q 120 ${12 + i * 38}, 360 ${28 + i * 38}`} />
        ))}
      </g>
      <text x={W / 2} y="44" fill={BONE} fontFamily="'JetBrains Mono', monospace" fontSize="9" fontWeight="700" textAnchor="middle" opacity="0.6">
        INCOMING QUESTION
      </text>
      {/* the incoming question card */}
      <g style={{ transition: 'opacity 500ms ease, transform 500ms ease', opacity: gone ? 0 : 1, transform: gone ? 'scale(0.9)' : 'scale(1)', transformOrigin: `${W / 2}px 110px` }}>
        <rect x="86" y="76" width="148" height="62" rx="10" fill={SIG} fillOpacity="0.16" stroke={SIG} strokeWidth="1.6" />
        <text x="160" y="104" fill={BONE} fontFamily="'Archivo', sans-serif" fontSize="13" fontWeight="800" fontStyle="italic" textAnchor="middle">
          RADIUS 500
        </text>
        <text x="160" y="124" fill={BONE} fontFamily="'JetBrains Mono', monospace" fontSize="10" fontWeight="700" textAnchor="middle" opacity="0.7">
          0:30
        </text>
        {/* v15 BOLT-053: the veto strikes with the ✗ — the same two-diagonal
            mark the v14 rails put on a vetoed card, not a horizontal line. */}
        <g
          data-testid="veto-demo-x"
          stroke={SIG}
          strokeWidth="4"
          strokeLinecap="round"
          style={{ transition: 'opacity 300ms ease', opacity: beat >= 1 ? 1 : 0 }}
        >
          <line x1="94" y1="82" x2="226" y2="132" />
          <line x1="226" y1="82" x2="94" y2="132" />
        </g>
      </g>
      <text x={W / 2} y="170" fill={SIG} fontFamily="'Archivo', sans-serif" fontSize="12" fontWeight="800" fontStyle="italic" textAnchor="middle" style={fade(beat >= 1, 300)}>
        VETOED
      </text>
    </>
  );
}

/** v16 BOLT-072 — the Airhorn: a phone erupting sound arcs, then the HONK. */
function airhornDemo({ beat }: Ctx) {
  const honking = beat >= 1;
  return (
    <>
      <rect x="0" y="0" width={W} height={H} fill={SLATE} />
      <text x={W / 2} y="34" fill={BONE} fontFamily="'JetBrains Mono', monospace" fontSize="9" fontWeight="700" textAnchor="middle" opacity="0.6">
        THE HIDER&apos;S PHONE
      </text>
      {/* the phone */}
      <g>
        <rect x="138" y="62" width="44" height="78" rx="8" fill="none" stroke={BONE} strokeWidth="1.6" opacity="0.8" />
        <rect x="152" y="68" width="16" height="3" rx="1.5" fill={BONE} opacity="0.5" />
      </g>
      {/* sound arcs, both sides */}
      <g fill="none" stroke={SIG} strokeWidth="3" strokeLinecap="round" style={fade(honking, 300)}>
        {[0, 1, 2].map((i) => (
          <path key={`l${i}`} d={`M ${126 - i * 14} ${84 - i * 6} Q ${112 - i * 16} 101, ${126 - i * 14} ${118 + i * 6}`} opacity={1 - i * 0.28} />
        ))}
        {[0, 1, 2].map((i) => (
          <path key={`r${i}`} d={`M ${194 + i * 14} ${84 - i * 6} Q ${208 + i * 16} 101, ${194 + i * 14} ${118 + i * 6}`} opacity={1 - i * 0.28} />
        ))}
      </g>
      {/* v19: the "HONK" word removed — the sound-wave arcs carry it. */}
      <text x={W / 2} y="170" fill={BONE} fontFamily="'JetBrains Mono', monospace" fontSize="8.5" fontWeight="700" textAnchor="middle" opacity="0.65" style={fade(beat >= 2, 300)}>
        NO SOUND? YOU GET THE CARD BACK
      </text>
    </>
  );
}

/** The renderer registry — a Record over the full DemoType union, so TypeScript
 *  forces every demo type to be handled. `HANDLED_DEMO_TYPES` is asserted equal
 *  to cardGuide's `DEMO_TYPES` by the registry test. */
const renderers: Record<DemoType, (ctx: Ctx) => ReactNode> = {
  radar: radarDemo,
  direction: directionDemo,
  quadrants: quadrantsDemo,
  borders: bordersDemo,
  thermometer: thermometerDemo,
  photo: photoDemo,
  freeze: freezeDemo, // v19 — per-curse demos (were all the generic 'curse-time')
  silence: silenceDemo,
  babel: babelDemo,
  'flying-blind': flyingBlindDemo,
  portal: portalDemo,
  bonus: bonusDemo,
  veto: vetoDemo,
  airhorn: airhornDemo, // v16 BOLT-072
  landmark: landmarkDemo, // v24 BOLT-118: the map-data cards
};

export const HANDLED_DEMO_TYPES: readonly DemoType[] = Object.keys(renderers) as DemoType[];

export function CardDemo({ demo }: { demo: DemoDescriptor }) {
  const [reduced] = useState(prefersReduced);
  const [inView, setInView] = useState(false);
  const [beat, setBeat] = useState(reduced ? 2 : 0);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el || typeof IntersectionObserver === 'undefined') {
      setInView(true);
      return;
    }
    const io = new IntersectionObserver(([e]) => setInView(e.isIntersecting), { threshold: 0.2 });
    io.observe(el);
    return () => io.disconnect();
  }, []);

  useEffect(() => {
    if (reduced || !inView) return; // paused off-screen / reduced-motion holds the after-frame
    const id = setInterval(() => setBeat((b) => (b + 1) % 3), 1200);
    return () => clearInterval(id);
  }, [reduced, inView]);

  const render = renderers[demo.type];
  return (
    <figure className="demo" ref={ref} data-testid="card-demo" data-demo-type={demo.type}>
      <svg className="demo__stage" viewBox={`0 0 ${W} ${H}`} role="img" aria-label={demo.caption} preserveAspectRatio="xMidYMid meet">
        {render ? render({ beat, param: demo.param }) : null}
      </svg>
      <figcaption className="demo__caption" data-testid="demo-caption">
        {demo.caption}
      </figcaption>
    </figure>
  );
}
